import type { BootstrapBundle, GitPlan } from "../setup/bootstrap-bundle-types.js";
import type { BootLogger, SessionClaims } from "./types.js";

/**
 * Ports entrypoint.sh:222-344 (bundle field extraction, JWT claim decode,
 * mode mapping, env injection + denylist).
 *
 * DELIBERATE DEVIATION from the bash on empty values: the literal entrypoint
 * exported most credential/identity fields UNCONDITIONALLY — `export
 * FOO=$(... | jq -r '.f // empty')` yields an empty-string-PRESENT variable
 * when the field is missing (jq's `// empty` only empties the substitution,
 * the `export` still happens). That covered CONVEYOR_TASK_TOKEN,
 * CONVEYOR_GITHUB_TOKEN/GH_TOKEN, ANTHROPIC_API_KEY,
 * CLOUDSDK_AUTH_ACCESS_TOKEN, REPO_OWNER/REPO_NAME/BRANCH,
 * CONVEYOR_SESSION_ID, and CONVEYOR_WORKSPACE_ID; only CONVEYOR_TASK_ID and
 * CONVEYOR_PROJECT_ID were truly absent-on-empty in bash too (guarded
 * export / explicit unset). vnext instead omits ANY empty-valued key
 * uniformly (`setIf` in buildChildEnv): absent is the safer contract —
 * presence-keyed consumers (gh, anthropic tooling) misread an empty-present
 * GH_TOKEN/ANTHROPIC_API_KEY as "credentials configured" — and every known
 * in-repo consumer checks truthiness, so the change is otherwise inert.
 *
 * `parseBundle` throws on a genuinely missing/wrong-type required field (so
 * boot fails loud instead of shipping "null" into a git URL or env var) and
 * normalizes `envVars: null` to `{}`. Unknown top-level keys are preserved
 * on the returned object — the bundle wire contract can grow fields this
 * package doesn't know about yet.
 */
function requireString(source: Record<string, unknown>, field: string): string {
  const value = source[field];
  if (typeof value !== "string") {
    throw new Error(`bundle missing field: ${field}`);
  }
  return value;
}

function parseGitPlan(raw: unknown): GitPlan {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("bundle missing field: gitPlan");
  }
  const obj = raw as Record<string, unknown>;
  const plan: GitPlan = {
    repoOwner: requireString(obj, "repoOwner"),
    repoName: requireString(obj, "repoName"),
    branch: requireString(obj, "branch"),
    baseBranch: requireString(obj, "baseBranch"),
  };
  if (typeof obj.checkoutRef === "string") {
    plan.checkoutRef = obj.checkoutRef;
  }
  return plan;
}

export function parseBundle(text: string): BootstrapBundle {
  const raw = JSON.parse(text) as Record<string, unknown>;
  const envVarsRaw = raw.envVars;
  const envVars: Record<string, string> =
    envVarsRaw !== null && envVarsRaw !== undefined && typeof envVarsRaw === "object"
      ? (envVarsRaw as Record<string, string>)
      : {};
  const typed = {
    sessionJwt: requireString(raw, "sessionJwt"),
    githubToken: requireString(raw, "githubToken"),
    anthropicKey: requireString(raw, "anthropicKey"),
    gcpToken: requireString(raw, "gcpToken"),
    envVars,
    gitPlan: parseGitPlan(raw.gitPlan),
  };
  return { ...raw, ...typed } as BootstrapBundle;
}

/**
 * Decodes the non-secret session identity claims out of the task JWT's
 * middle segment — base64url alphabet (`-`/`_`), padding restored before
 * decode. Ports the inline `node -e` one-liner at entrypoint.sh:236. Any
 * failure (too few segments, invalid base64, non-JSON payload) yields `{}`
 * rather than throwing: an unreadable JWT degrades to "no claims", it never
 * aborts boot — the bootstrap poll step already validated the token against
 * the API before this runs.
 */
export function decodeJwtClaims(jwt: string): SessionClaims {
  try {
    const segment = jwt.split(".")[1] ?? "";
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const json = Buffer.from(padded, "base64").toString("utf8");
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    return parsed as SessionClaims;
  } catch {
    return {};
  }
}

// Mode mapping, comments carried from entrypoint.sh:261-287. Order is load-bearing —
// a wrong mapping here crash-looped pods every 10s (agent CLI exits "Invalid
// CONVEYOR_MODE"). Do not reorder without re-reading the history in that range.
export function resolveModeEnv(claims: SessionClaims): Record<string, string> {
  // Review sessions are keyed on the session MODE claim, not role; the runner
  // mode is spelled "code-review" — bare "review" crash-loops the CLI.
  if (claims.mode === "review") return { CONVEYOR_MODE: "code-review" };
  if (claims.mode === "pack") return { CONVEYOR_MODE: "pack" };
  // adhoc is also task-less with a projectId — it must win over the pm branch.
  if (claims.mode === "adhoc") return { CONVEYOR_MODE: "adhoc" };
  if (!claims.taskId && claims.projectId) return { CONVEYOR_MODE: "pm" };
  return {};
}

// A bundle key that isn't a valid shell/env identifier would make `export`
// eval arbitrary content in the old bash world; the vnext child-env object
// has no such injection risk, but we keep the same validation so a garbage
// key can never silently shadow something unexpected. A handful of names
// would hijack the boot itself (PATH swaps every binary below; LD_PRELOAD
// injects code into them) — denylisted regardless of validity.
export const ENV_KEY_DENYLIST: ReadonlySet<string> = new Set([
  "PATH",
  "HOME",
  "LD_PRELOAD",
  "SHELL",
]);
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export interface BuildChildEnvOptions {
  base: NodeJS.ProcessEnv;
  bundle: BootstrapBundle;
  claims: SessionClaims;
  hostname: string;
  log: BootLogger;
  /** default "/workspaces/repo" */
  workspaceDir?: string;
}

export function buildChildEnv(opts: BuildChildEnvOptions): Record<string, string> {
  const { base, bundle, claims, hostname, log } = opts;
  const child: Record<string, string> = {};
  for (const [k, v] of Object.entries(base)) {
    if (typeof v === "string") child[k] = v;
  }
  for (const [key, value] of Object.entries(bundle.envVars ?? {})) {
    if (!IDENTIFIER_RE.test(key)) {
      log.warn(`[boot] WARN skipping invalid env key from bundle: '${key}'`);
      continue;
    }
    if (ENV_KEY_DENYLIST.has(key)) {
      log.warn(`[boot] WARN skipping denylisted env key from bundle: '${key}'`);
      continue;
    }
    child[key] = value;
  }
  const setIf = (k: string, v: string | undefined): void => {
    if (v) child[k] = v;
  };
  setIf("CONVEYOR_TASK_TOKEN", bundle.sessionJwt);
  setIf("CONVEYOR_GITHUB_TOKEN", bundle.githubToken);
  setIf("GH_TOKEN", bundle.githubToken);
  setIf("ANTHROPIC_API_KEY", bundle.anthropicKey);
  setIf("CLOUDSDK_AUTH_ACCESS_TOKEN", bundle.gcpToken);
  setIf("REPO_OWNER", bundle.gitPlan.repoOwner);
  setIf("REPO_NAME", bundle.gitPlan.repoName);
  setIf("BRANCH", bundle.gitPlan.branch);
  // CONVEYOR_TASK_ID is absent (never empty) for task-less sessions.
  setIf("CONVEYOR_TASK_ID", claims.taskId);
  setIf("CONVEYOR_PROJECT_ID", claims.projectId);
  setIf("CONVEYOR_SESSION_ID", claims.sessionId);
  setIf("CONVEYOR_WORKSPACE_ID", claims.workspaceId);
  Object.assign(child, resolveModeEnv(claims));
  child.CLAUDESPACE_NAME = hostname;
  child.CONVEYOR_WORKSPACE = opts.workspaceDir ?? "/workspaces/repo";
  child.CONVEYOR_BOOT_VNEXT = "1";
  // POD_BOOTSTRAP_TOKEN and CONVEYOR_API_URL are never touched here — they pass
  // through unchanged from `base` (copied above). POD_BOOTSTRAP_TOKEN is the
  // credential-refresh key: the agent re-polls with it to swap in a fresh
  // githubToken/sessionJwt in place (entrypoint.sh:338-340).
  return child;
}
